/**
 * Pulse — GameOn's anonymous internal analytics layer.
 * Fire-and-forget; never throws into the host page.
 *
 * Privacy contract: stores source host, URL path, event name, referrer
 * category, and 2-letter country derived at the edge. No PII, no IPs,
 * no cookies, no fingerprints, no query strings.
 *
 * Optional `meta` carries ad-tracking context per
 * docs/ad-tracking-contract.md — server schema only accepts the listed
 * keys (placement, slot_id, surface, sponsor_id, artifact).
 */
const PULSE_ENDPOINT =
  "https://etrspsphgbkapczqxboy.supabase.co/functions/v1/pulse-event";

export type PulseMeta = {
  placement?: string;
  slot_id?: string;
  surface?: string;
  sponsor_id?: string;
  artifact?: string;
};

export function pulse(
  event: string,
  path?: string,
  ref?: string | null,
  meta?: PulseMeta,
) {
  try {
    if (typeof window === "undefined") return;
    const payload: Record<string, unknown> = {
      source: window.location.host,
      path: path ?? window.location.pathname,
      event,
      ref: ref ?? document.referrer ?? null,
    };
    if (meta && Object.keys(meta).length > 0) {
      payload.meta = meta;
    }
    const body = JSON.stringify(payload);
    // Defer to idle time so analytics never contends with the RPC
    // that's actually painting the page.
    const send = () => {
      // Prefer sendBeacon when available — non-blocking, survives navigation.
      try {
        if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
          // text/plain is a CORS-safelisted content type, so the beacon needs no
          // preflight. sendBeacon CANNOT complete a preflight: with
          // type "application/json" the request is dropped and sendBeacon still
          // returns true, so the fetch fallback below never fires and the event
          // is lost silently. The receiving edge function parses with req.json(),
          // which ignores the Content-Type header, so nothing changes server-side.
          const blob = new Blob([body], { type: "text/plain;charset=UTF-8" });
          if (navigator.sendBeacon(PULSE_ENDPOINT, blob)) return;
        }
      } catch {
        /* fall through to fetch */
      }
      void fetch(PULSE_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => {
        /* never throw into the host page */
      });
    };
    const ric = (window as any).requestIdleCallback as
      | ((cb: () => void, opts?: { timeout: number }) => number)
      | undefined;
    if (typeof ric === "function") {
      ric(send, { timeout: 2000 });
    } else {
      setTimeout(send, 0);
    }
  } catch {
    /* noop */
  }
}

